import { cacheProvider } from '@/utils/googleTranslateCacheProvider';

export class Translator {
  constructor(private defaultLanguage: string, private googleApiKey: string) {}

  async translate(text: string, translateTo: string, isHtml = false): Promise<string> {
    if (this.defaultLanguage === translateTo) return text;

    const cached = await cacheProvider.get(translateTo, text);
    if (cached) return cached;

    const translated = await this.fetchAndCacheTranslation(text, translateTo, isHtml);
    return translated ?? text;
  }

  private async fetchAndCacheTranslation(
    text: string,
    translateTo: string,
    isHtml: boolean
  ): Promise<string | undefined> {
    try {
      const translated = await this.fetchGoogleTranslation(text, translateTo, isHtml);
      if (translated) {
        await cacheProvider.set(translateTo, text, translated);
      }
      return translated;
    } catch (error) {
      console.error('Translation error:', error);
      return undefined;
    }
  }

  private async fetchGoogleTranslation(
    text: string,
    translateTo: string,
    isHtml: boolean
  ): Promise<string | undefined> {
    const endpoint = new URL('https://translation.googleapis.com/language/translate/v2');
    endpoint.searchParams.set('source', this.defaultLanguage);
    endpoint.searchParams.set('target', translateTo);
    endpoint.searchParams.set('key', this.googleApiKey);
    endpoint.searchParams.set('q', text);
    endpoint.searchParams.set('format', isHtml ? 'html' : 'text');

    const response = await fetch(endpoint.toString());
    if (!response.ok) {
      throw new Error(`Google Translate API error: ${response.statusText}`);
    }

    const json = await response.json();
    return json?.data?.translations?.[0]?.translatedText;
  }
}
